import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaEllipsisV } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  listMyNotifications,
  listNotifications,
  markMyNotificationAsRead,
  markAllMyNotificationsAsRead,
  updateMyNotificationPreferences,
  deleteMyNotification,
  deleteNotification,
} from "../../api/notifications";
import NotificationDetailsModal from "../../components/notifications/NotificationDetailsModal";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import ExpandableSearch from "../../components/ExpandableSearch";
import { getUserFacingError } from "../../utils/userFacingError";
import { getNotificationActionLabel, getNotificationActionUrl } from "../../utils/notificationNavigation";

const formatDateLabel = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Earlier";
  }

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  }

  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }

  return date.toLocaleDateString(undefined, {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getNotificationDate = (notification) =>
  notification.deliveredAt ||
  notification.sentAt ||
  notification.createdAt ||
  new Date().toISOString();

const groupNotificationsByDate = (notifications) =>
  notifications.reduce((groups, notification) => {
    const label = formatDateLabel(getNotificationDate(notification));
    groups[label] = groups[label] || [];
    groups[label].push(notification);
    return groups;
  }, {});

const NotificationCard = ({ item, onOpen, onAction }) => {
  const isUnread = item.isRead === false;
  const actionUrl = getNotificationActionUrl(item);

  return (
    <article className="mb-3 flex w-full items-center justify-between rounded-xl bg-white p-4 text-left shadow-sm transition hover:bg-gray-50">
      <div className="flex min-w-0 items-center">
        <button type="button" onClick={() => onOpen(item)} className="min-w-0 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-gray-900">
              {item.title || "Notification"}
            </p>
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium capitalize text-blue-700">
              {item.type || "general"}
            </span>
          </div>
          <p className="mt-1 line-clamp-2 text-sm text-gray-700">
            {item.message}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {formatTime(getNotificationDate(item))}
          </p>
        </button>
      </div>
      <div className="ml-3 flex flex-none items-center gap-3 sm:ml-4">
        {actionUrl ? <button type="button" onClick={() => onAction(item)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#1234CD] px-2.5 py-1.5 text-xs font-semibold text-[#1234CD] hover:bg-blue-50"><FiExternalLink />{getNotificationActionLabel(item)}</button> : <button type="button" onClick={() => onOpen(item)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">View details</button>}
        {isUnread ? (
          <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
        ) : null}
        <FaEllipsisV className="text-gray-400" />
      </div>
    </article>
  );
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inboxMode, setInboxMode] = useState("employee");
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [retentionMonths, setRetentionMonths] = useState(2);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const loadNotifications = useCallback(async () => {
    setLoading(true);

    try {
      const response = await listMyNotifications({
        page: currentPage,
        limit: 15,
      });

      setInboxMode("employee");
      setNotifications(response.notifications || []);
      setUnreadCount(response.unreadCount || 0);
      setTotalPages(response.pagination?.pages || 1);
      setRetentionMonths(response.notificationPreferences?.retentionMonths ?? 2);
    } catch (employeeError) {
      try {
        const response = await listNotifications({
          page: currentPage,
          limit: 15,
        });

        setInboxMode("admin");
        setNotifications(response.notifications || []);
        setUnreadCount(0);
        setTotalPages(response.pagination?.pages || 1);
      } catch (adminError) {
        toast.error(getUserFacingError(adminError || employeeError, "Unable to load notifications."));
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const visibleNotifications = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return notifications;
    return notifications.filter((notification) =>
      [notification.title, notification.message, notification.type, notification.status]
        .some((value) => String(value || "").toLowerCase().includes(query)),
    );
  }, [notifications, searchTerm]);

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(visibleNotifications),
    [visibleNotifications]
  );

  const handleOpenNotification = async (notification) => {
    setSelectedNotification(notification);

    if (inboxMode === "employee" && notification.isRead === false) {
      setNotifications((currentNotifications) =>
        currentNotifications.map((item) =>
          item.id === notification.id ? { ...item, isRead: true } : item
        )
      );
      setUnreadCount((count) => Math.max(0, count - 1));

      try {
        await markMyNotificationAsRead(notification.id);
      } catch (error) {
        toast.error(getUserFacingError(error, "Unable to mark notification as read."));
        loadNotifications();
      }
    }

  };

  const handleNotificationAction = async (notification) => {
    const actionUrl = getNotificationActionUrl(notification);
    if (!actionUrl) return;
    if (inboxMode === "employee" && notification.isRead === false) {
      setNotifications((items) => items.map((item) => item.id === notification.id ? { ...item, isRead: true } : item));
      setUnreadCount((count) => Math.max(0, count - 1));
      try { await markMyNotificationAsRead(notification.id); } catch { /* navigation should still work */ }
    }
    navigate(actionUrl);
  };

  const handleDeleteNotification = async (notification) => {
    if (inboxMode === "employee") await deleteMyNotification(notification.id);
    else await deleteNotification(notification.id);
    setNotifications((items) => items.filter((item) => item.id !== notification.id));
    toast.success("Notification deleted");
  };

  const handleMarkAllAsRead = async () => {
    if (!unreadCount || markingAllRead) return;
    try {
      setMarkingAllRead(true);
      await markAllMyNotificationsAsRead();
      setNotifications((items) => items.map((item) => ({ ...item, isRead: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error(getUserFacingError(error, "Unable to mark all notifications as read."));
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleRetentionChange = async (event) => {
    const nextRetention = Number(event.target.value);
    const previousRetention = retentionMonths;
    setRetentionMonths(nextRetention);
    try {
      setSavingPreferences(true);
      const response = await updateMyNotificationPreferences(nextRetention);
      setRetentionMonths(response.retentionMonths || nextRetention);
      toast.success(`Notifications will be kept for ${nextRetention} month${nextRetention === 1 ? "" : "s"}.`);
      await loadNotifications();
    } catch (error) {
      setRetentionMonths(previousRetention);
      toast.error(getUserFacingError(error, "Unable to update notification preferences."));
    } finally {
      setSavingPreferences(false);
    }
  };

  return (
    <div className="min-h-screen p-6 font-Outfit">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            All Notifications
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {inboxMode === "employee"
              ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`
              : "Admin activity and broadcast notifications"}
          </p>
        </div>
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
        <ExpandableSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search notifications" label="Search notifications" className="flex-1 sm:flex-none" />
        {inboxMode === "employee" ? <>
          <button type="button" onClick={handleMarkAllAsRead} disabled={!unreadCount || markingAllRead} className="rounded-lg border border-[#1234CD] px-3 py-2 text-sm font-medium text-[#1234CD] disabled:cursor-not-allowed disabled:opacity-50">
            {markingAllRead ? "Marking..." : "Mark all as read"}
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            Keep
            <select value={retentionMonths} onChange={handleRetentionChange} disabled={savingPreferences} className="rounded-lg border border-gray-300 px-2 py-2 text-sm text-gray-900" aria-label="Notification retention">
              {[1, 2, 3, 4].map((months) => <option key={months} value={months}>{months === 4 ? "4 months (maximum)" : `${months} month${months === 1 ? "" : "s"}`}</option>)}
            </select>
          </label>
        </> : null}
        <button
          type="button"
          onClick={loadNotifications}
          className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Refresh
        </button>
        </div>
      </div>

      {loading ? (
        <FetchLoadingState message="Loading notifications..." className="min-h-[55vh] rounded-xl bg-white shadow-sm" />
      ) : visibleNotifications.length === 0 ? (
        <div className="rounded-xl bg-white p-10 text-center text-sm text-gray-500 shadow-sm">
          {searchTerm ? "No matching notifications found." : "No notifications yet."}
        </div>
      ) : (
        Object.entries(groupedNotifications).map(([label, items]) => (
          <section key={label} className="mb-8">
            <h2 className="mb-4 text-sm font-semibold text-gray-900">
              {label}
            </h2>
            {items.map((item) => (
              <NotificationCard
                key={item.id}
                item={item}
                onOpen={handleOpenNotification}
                onAction={handleNotificationAction}
              />
            ))}
          </section>
        ))
      )}

      {totalPages > 1 ? (
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            className="rounded-lg border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
            className="rounded-lg border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}

      <NotificationDetailsModal
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
        onDelete={handleDeleteNotification}
      />

    </div>
  );
};

export default NotificationsPage;
